import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  IconButton,
  Fab,
  Alert,
  Paper,
  Chip,
  TextField
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Create as CreateIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  AttachMoney as MoneyIcon,
  FormatListBulleted as ListIcon,
  Home as HomeIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  TextFields as TextFieldsIcon,
  Visibility as VisibilityIcon,
  ShortText as ShortTextIcon,
  Email as EmailIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { Document, Page, pdfjs } from 'react-pdf';
import type { PDFPageProxy, PDFDocumentProxy } from 'pdfjs-dist';
import { PDFDocument, rgb } from 'pdf-lib';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface FieldMarker {
  id: string;
  type: 'signature' | 'variable' | 'text';
  x: number; // Relative coordinate (0-1)
  y: number; // Relative coordinate (0-1)
  pageNumber: number;
  absoluteX: number;
  absoluteY: number;
  variableName?: string; // For type='variable': amount, lineItems, propertyAddress, customerName, date
  customText?: string; // For type='text': user-entered text to embed in PDF
}

interface PDFSignatureModalProps {
  open: boolean;
  onClose: () => void;
  pdfUrl: string;
  fileName?: string;
  onSaveSignaturePlacements: (markers: FieldMarker[], modifiedPdfFile?: File) => void;
  initialFieldMarkers?: FieldMarker[]; // Optional prop for existing field placements
}

// Predefined variables
const PREDEFINED_VARIABLES = [
  { name: 'amount', label: 'Amount', icon: MoneyIcon, color: '#4caf50' },
  { name: 'lineItems', label: 'Line Items', icon: ListIcon, color: '#ff9800' },
  { name: 'propertyAddress', label: 'Property Address', icon: HomeIcon, color: '#2196f3' },
  { name: 'customerName', label: 'Customer Name', icon: PersonIcon, color: '#9c27b0' },
  { name: 'email', label: 'Email', icon: EmailIcon, color: '#00bcd4' },
  { name: 'phone', label: 'Phone', icon: PhoneIcon, color: '#4caf50' },
  { name: 'date', label: 'Date', icon: CalendarIcon, color: '#f44336' },
  { name: 'initial', label: 'Initial', icon: ShortTextIcon, color: '#00bcd4' }
];

const PDFSignatureModal: React.FC<PDFSignatureModalProps> = ({
  open,
  onClose,
  pdfUrl,
  fileName = 'Document',
  onSaveSignaturePlacements,
  initialFieldMarkers = []
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [fieldMarkers, setFieldMarkers] = useState<FieldMarker[]>([]);
  const [activeFieldType, setActiveFieldType] = useState<'signature' | 'variable' | 'text' | null>(null);
  const [activeVariableName, setActiveVariableName] = useState<string | null>(null);
  const [pageScale, setPageScale] = useState(1.0); // Start at 100% zoom
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoverPosition, setHoverPosition] = useState<{x: number, y: number, pageNumber: number, type: 'signature' | 'variable' | 'text', variableName?: string} | null>(null);
  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>(null);
  const [pageProxies, setPageProxies] = useState<{[key: number]: PDFPageProxy}>({});
  const [editingTextField, setEditingTextField] = useState<{
    id: string;
    x: number;
    y: number;
    pageNumber: number;
    relativeX: number;
    relativeY: number;
    absoluteX: number;
    absoluteY: number;
    text: string;
  } | null>(null);
  const [draggingMarker, setDraggingMarker] = useState<{
    id: string;
    offsetX: number;
    offsetY: number;
  } | null>(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewNumPages, setPreviewNumPages] = useState<number | null>(null);

  const pageRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  // Convert click coordinates to DocuSign coordinates (zoom-independent)
  const convertToDocuSignCoordinates = (clickX: number, clickY: number, pageNumber: number, currentScale: number) => {
    const pageProxy = pageProxies[pageNumber];
    if (!pageProxy) {
      return { docuSignX: Math.round(clickX), docuSignY: Math.round(clickY) };
    }

    // Normalize click position to remove zoom effect
    const docuSignX = clickX / currentScale;
    const docuSignY = clickY / currentScale;

    return {
      docuSignX: Math.round(docuSignX),
      docuSignY: Math.round(docuSignY)
    };
  };

  const onDocumentLoadSuccess = (pdf: any) => {
    const numPages = pdf.numPages;
    setNumPages(numPages);
    setPdfDocument(pdf);
    setIsLoading(false);
    setError(null);

    // Load page proxies to get actual PDF dimensions
    const loadPageProxies = async () => {
      const proxies: {[key: number]: PDFPageProxy} = {};
      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        try {
          const page = await pdf.getPage(pageNum);
          proxies[pageNum] = page;
        } catch (error) {
          console.error(`Failed to load page ${pageNum}:`, error);
        }
      }
      setPageProxies(proxies);

      // Log PDF document info for coordinate debugging
      console.log('📝 PDF Document Loaded:', {
        numPages,
        scale: pageScale,
        pageProxies: Object.keys(proxies).length,
        timestamp: new Date().toISOString()
      });
    };

    loadPageProxies();
  };

  const onDocumentLoadError = (error: any) => {
    console.error('Error loading PDF:', error);
    setError('Failed to load PDF document. Please try again.');
    setIsLoading(false);
  };

  const handlePageClick = useCallback(async (event: React.MouseEvent, pageNumber: number) => {
    // Only allow clicks when an active field type is selected
    if (!activeFieldType) return;
    
    const pageElement = pageRefs.current[pageNumber];
    if (!pageElement) return;

    const rect = pageElement.getBoundingClientRect();
    const absoluteX = event.clientX - rect.left;
    const absoluteY = event.clientY - rect.top;

    // Get the current rendered page dimensions
    const renderedPageWidth = rect.width;
    const renderedPageHeight = rect.height;

    // Validate click is within bounds
    if (absoluteX < 0 || absoluteY < 0 || absoluteX > renderedPageWidth || absoluteY > renderedPageHeight) {
      console.warn('Click outside page bounds, ignoring');
      return;
    }

    // Convert to relative coordinates (0-1 range)
    const relativeX = absoluteX / renderedPageWidth;
    const relativeY = absoluteY / renderedPageHeight;

    // Validate relative coordinates are in expected range
    if (relativeX < 0 || relativeX > 1 || relativeY < 0 || relativeY > 1) {
      console.error('Invalid relative coordinates calculated', { relativeX, relativeY });
      return;
    }

    // Convert to DocuSign coordinates (zoom-independent)
    const docuSignCoords = convertToDocuSignCoordinates(
      absoluteX, absoluteY, pageNumber, pageScale
    );

    // For custom text, create editable marker immediately
    if (activeFieldType === 'text') {
      const newTextFieldId = `text-${Date.now()}-${Math.random()}`;
      // Store viewport coordinates for fixed positioning
      const viewportX = event.clientX;
      const viewportY = event.clientY;
      
      setEditingTextField({
        id: newTextFieldId,
        x: viewportX,
        y: viewportY,
        pageNumber,
        relativeX,
        relativeY,
        absoluteX: docuSignCoords.docuSignX,
        absoluteY: docuSignCoords.docuSignY,
        text: ''
      });
      setHoverPosition(null);
      return;
    }

    const newMarker: FieldMarker = {
      id: `${activeFieldType}-${Date.now()}-${Math.random()}`,
      type: activeFieldType,
      x: relativeX,
      y: relativeY,
      pageNumber,
      absoluteX: docuSignCoords.docuSignX,
      // For variables, add +12 to Y coordinate (half of 24px marker height) to position text at marker center
      absoluteY: activeFieldType === 'variable' ? docuSignCoords.docuSignY + 12 : docuSignCoords.docuSignY,
      // Include variableName for both variable types and signature types with variableName (like initial)
      ...((activeFieldType === 'variable' || activeVariableName) && { variableName: activeVariableName || undefined })
    };

    setFieldMarkers(prev => [...prev, newMarker]);
    setHoverPosition(null);

    // Detailed logging for coordinate comparison
    console.log(`\n${activeFieldType} placed:`, {
      pageNumber: pageNumber,
      absoluteX: newMarker.absoluteX,
      absoluteY: newMarker.absoluteY,
      relativeX: relativeX,
      relativeY: relativeY,
      renderedPageHeight: renderedPageHeight,
      ...(activeFieldType === 'variable' && { 
        variableName: activeVariableName,
        rawY: docuSignCoords.docuSignY,
        adjustedY: newMarker.absoluteY,
        offset: '+12px (half marker height)'
      }),
      note: activeFieldType === 'variable' ? 'Y adjusted +12 for text centering' : 'absoluteX/Y from convertToDocuSignCoordinates'
    });
  }, [pageScale, convertToDocuSignCoordinates, activeFieldType, activeVariableName]);

  const handlePageMouseMove = useCallback((event: React.MouseEvent, pageNumber: number) => {
    if (!activeFieldType) return;
    
    const pageElement = pageRefs.current[pageNumber];
    if (!pageElement) return;

    const rect = pageElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    setHoverPosition({
      x,
      y,
      pageNumber,
      type: activeFieldType,
      // Include variableName for both variable types and signature types with variableName (like initial)
      ...((activeFieldType === 'variable' || activeVariableName) && { variableName: activeVariableName || undefined })
    });
  }, [activeFieldType, activeVariableName]);

  const handlePageMouseLeave = useCallback(() => {
    setHoverPosition(null);
  }, []);

  const removeFieldMarker = (markerId: string) => {
    setFieldMarkers(prev => prev.filter(marker => marker.id !== markerId));
  };

  const handleMarkerMouseDown = (e: React.MouseEvent, markerId: string, pageNumber: number) => {
    e.stopPropagation();
    const pageElement = pageRefs.current[pageNumber];
    if (!pageElement) return;

    const rect = pageElement.getBoundingClientRect();
    const marker = fieldMarkers.find(m => m.id === markerId);
    if (!marker) return;

    const offsetX = e.clientX - rect.left - (marker.x * rect.width);
    const offsetY = e.clientY - rect.top - (marker.y * rect.height);

    setDraggingMarker({
      id: markerId,
      offsetX,
      offsetY
    });
  };

  const handleMarkerDrag = useCallback((e: React.MouseEvent, pageNumber: number) => {
    if (!draggingMarker) return;

    const pageElement = pageRefs.current[pageNumber];
    if (!pageElement) return;

    const rect = pageElement.getBoundingClientRect();
    const newX = e.clientX - rect.left - draggingMarker.offsetX;
    const newY = e.clientY - rect.top - draggingMarker.offsetY;

    const relativeX = Math.max(0, Math.min(1, newX / rect.width));
    const relativeY = Math.max(0, Math.min(1, newY / rect.height));

    setFieldMarkers(prev => prev.map(marker => {
      if (marker.id === draggingMarker.id) {
        const docuSignCoords = convertToDocuSignCoordinates(
          relativeX * rect.width,
          relativeY * rect.height,
          pageNumber,
          pageScale
        );
        return {
          ...marker,
          x: relativeX,
          y: relativeY,
          absoluteX: docuSignCoords.docuSignX,
          // For variables, add +12 to Y coordinate (half of 24px marker height) to position text at marker center
          absoluteY: marker.type === 'variable' ? docuSignCoords.docuSignY + 12 : docuSignCoords.docuSignY
        };
      }
      return marker;
    }));
  }, [draggingMarker, pageScale, convertToDocuSignCoordinates]);

  const handleMarkerMouseUp = useCallback(() => {
    setDraggingMarker(null);
  }, []);

  const generatePreviewPdf = async () => {
    try {
      const textFields = fieldMarkers.filter(m => m.type === 'text');
      const variableFields = fieldMarkers.filter(m => m.type === 'variable');

      if (textFields.length === 0 && variableFields.length === 0) {
        alert('No custom text fields or variables to preview. Preview will show the original PDF.');
        setPreviewPdfUrl(pdfUrl);
        setShowPreview(true);
        return;
      }

      const pdfBytes = await fetch(pdfUrl).then(res => res.arrayBuffer());
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const pages = pdfDoc.getPages();

      // Sample data for variable preview
      const sampleData: {[key: string]: string} = {
        amount: '$15,750.00',
        lineItems: 'Kitchen renovation, Bathroom tiles, Flooring installation',
        propertyAddress: '123 Main Street, Anytown, CA 90210',
        customerName: 'John Smith',
        email: 'john.smith@example.com',
        phone: '(555) 123-4567',
        date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        initial: 'J.S.'
      };

      // Process custom text fields
      for (const textField of textFields) {
        const page = pages[textField.pageNumber - 1];
        if (!page) continue;

        const { width, height } = page.getSize();
        const pdfX = textField.x * width;
        // Position text to match marker visual position
        // Using -12 offset (half of 24px marker height) to align with marker text
        const pdfY = height - (textField.y * height) - 12;

        page.drawText(textField.customText || '', {
          x: pdfX,
          y: pdfY,
          size: 11,
          color: rgb(0, 0, 0)
        });
      }

      // Process variable fields
      for (const variableField of variableFields) {
        const page = pages[variableField.pageNumber - 1];
        if (!page) continue;

        const { width, height } = page.getSize();
        const pdfX = variableField.x * width;
        // Position text to match marker visual position
        // Using -12 offset (half of 24px marker height) to align with marker text
        const pdfY = height - (variableField.y * height) - 12;

        const variableText = sampleData[variableField.variableName || ''] || `[${variableField.variableName || 'Unknown'}]`;

        page.drawText(variableText, {
          x: pdfX,
          y: pdfY,
          size: 11,
          color: rgb(0, 0.4, 0.8) // Blue color to distinguish variables from custom text
        });
      }

      const previewBytes = await pdfDoc.save();
      const previewBlob = new Blob([previewBytes as any], { type: 'application/pdf' });
      const previewUrl = URL.createObjectURL(previewBlob);

      if (previewPdfUrl) {
        URL.revokeObjectURL(previewPdfUrl);
      }

      setPreviewPdfUrl(previewUrl);
      setShowPreview(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      setError('Failed to generate preview. Please try again.');
    }
  };

  const handleSaveEditingTextField = () => {
    if (!editingTextField || !editingTextField.text) {
      // Cancel if no text entered
      setEditingTextField(null);
      return;
    }

    const newMarker: FieldMarker = {
      id: editingTextField.id,
      type: 'text',
      x: editingTextField.relativeX,
      y: editingTextField.relativeY,
      pageNumber: editingTextField.pageNumber,
      absoluteX: editingTextField.absoluteX,
      absoluteY: editingTextField.absoluteY,
      customText: editingTextField.text // Preserve exact user input
    };

    setFieldMarkers(prev => [...prev, newMarker]);
    
    console.log('Custom text placed:', {
      pageNumber: newMarker.pageNumber,
      absoluteX: newMarker.absoluteX,
      absoluteY: newMarker.absoluteY,
      relativeX: newMarker.x,
      relativeY: newMarker.y,
      text: newMarker.customText,
      textLength: newMarker.customText?.length
    });

    setEditingTextField(null);
  };

  const handleCancelEditingTextField = () => {
    setEditingTextField(null);
  };

  const handleSave = async () => {
    // Organize markers by type for clear logging
    const signaturePlacements = fieldMarkers.filter(m => m.type === 'signature');
    const predefinedVariables = fieldMarkers.filter(m => m.type === 'variable');
    const customTextFields = fieldMarkers.filter(m => m.type === 'text');

    console.log('\n========== PDF FIELD PLACEMENTS ==========\n');
    
    // Log Signature Placements
    if (signaturePlacements.length > 0) {
      console.log('📝 SIGNATURE PLACEMENTS:', signaturePlacements.length);
      signaturePlacements.forEach((marker, index) => {
        console.log(`  ${index + 1}. Page ${marker.pageNumber} - X: ${marker.absoluteX}, Y: ${marker.absoluteY}`);
      });
      console.log('');
    }

    // Log Predefined Variables
    if (predefinedVariables.length > 0) {
      console.log('🏷️  PREDEFINED VARIABLES:', predefinedVariables.length);
      predefinedVariables.forEach((marker, index) => {
        const variableLabel = PREDEFINED_VARIABLES.find(v => v.name === marker.variableName)?.label || marker.variableName;
        console.log(`  ${index + 1}. ${variableLabel} - Page ${marker.pageNumber}, X: ${marker.absoluteX}, Y: ${marker.absoluteY}`);
      });
      console.log('');
    }

    // Log Custom Text Fields
    if (customTextFields.length > 0) {
      console.log('✏️  CUSTOM TEXT FIELDS:', customTextFields.length);
      customTextFields.forEach((marker, index) => {
        console.log(`  ${index + 1}. "${marker.customText}" - Page ${marker.pageNumber}, X: ${marker.absoluteX}, Y: ${marker.absoluteY}`);
      });
      console.log('');
    }

    console.log('==========================================\n');

    // Check if there are any custom text fields that need PDF modification
    const textFields = fieldMarkers.filter(m => m.type === 'text');
    
    if (textFields.length > 0) {
      try {
        // Fetch the original PDF
        const pdfBytes = await fetch(pdfUrl).then(res => res.arrayBuffer());
        const pdfDoc = await PDFDocument.load(pdfBytes);
        const pages = pdfDoc.getPages();

        // Embed text fields into the PDF
        for (const textField of textFields) {
          const page = pages[textField.pageNumber - 1]; // Pages are 0-indexed
          if (!page) continue;

          const { width, height } = page.getSize();
          
          // Convert relative coordinates to PDF coordinate system
          // PDF coordinate system has origin at bottom-left, Y increases upward
          const pdfX = textField.x * width;
          // Position text to match marker visual position
          // Using -12 offset (half of 24px marker height) to align with marker text
          const pdfY = height - (textField.y * height) - 12;

          const textToEmbed = textField.customText || '';
          
          console.log('Embedding text in PDF:', {
            input: textField.customText,
            inputLength: textField.customText?.length,
            actualText: textToEmbed,
            actualLength: textToEmbed.length,
            position: { x: pdfX, y: pdfY },
            pageNumber: textField.pageNumber,
            yOffset: -12
          });

          page.drawText(textToEmbed, {
            x: pdfX,
            y: pdfY,
            size: 11, // Match typical PDF body text size
            color: rgb(0, 0, 0)
          });
        }

        // Save the modified PDF
        const modifiedPdfBytes = await pdfDoc.save();
        const modifiedPdfBlob = new Blob([modifiedPdfBytes as any], { type: 'application/pdf' });
        const modifiedPdfFile = new File([modifiedPdfBlob], fileName || 'modified.pdf', { 
          type: 'application/pdf' 
        });

        console.log('PDF modified with custom text fields');
        onSaveSignaturePlacements(fieldMarkers, modifiedPdfFile);
      } catch (error) {
        console.error('Error modifying PDF:', error);
        setError('Failed to embed text in PDF. Please try again.');
        return;
      }
    } else {
      // No custom text fields, just pass markers
      onSaveSignaturePlacements(fieldMarkers);
    }

    handleClose();
  };

  const handleClose = () => {
    setFieldMarkers([]);
    setActiveFieldType(null);
    setActiveVariableName(null);
    setEditingTextField(null);
    setDraggingMarker(null);
    setShowPreview(false);
    setPreviewNumPages(null);
    if (previewPdfUrl) {
      URL.revokeObjectURL(previewPdfUrl);
      setPreviewPdfUrl(null);
    }
    setError(null);
    setIsLoading(true);
    setNumPages(null);
    onClose();
  };

  // Effect to populate field markers with initial values when modal opens
  React.useEffect(() => {
    if (open && initialFieldMarkers.length > 0) {
      console.log('Populating with initial field markers:', initialFieldMarkers);
      setFieldMarkers(initialFieldMarkers);
    } else if (open) {
      setFieldMarkers([]);
    }
  }, [open, initialFieldMarkers]);

  // Effect to recalculate relative coordinates for initial markers when PDF loads
  React.useEffect(() => {
    if (numPages && pageProxies && Object.keys(pageProxies).length > 0 && initialFieldMarkers.length > 0) {
      const updatedMarkers = initialFieldMarkers.map(marker => {
        const pageProxy = pageProxies[marker.pageNumber];
        if (pageProxy) {
          const viewport = pageProxy.getViewport({ scale: 1.0 });
          const pdfWidth = viewport.width;
          const pdfHeight = viewport.height;

          // Calculate proper relative coordinates from absolute coordinates
          const relativeX = marker.absoluteX / pdfWidth;

          // For variable fields, subtract the 12px offset that was added during creation
          // This ensures proper alignment with the original click position
          const adjustedAbsoluteY = marker.type === 'variable'
            ? marker.absoluteY - 12
            : marker.absoluteY;
          const relativeY = adjustedAbsoluteY / pdfHeight;

          console.log('Coordinate conversion debug:', {
            markerId: marker.id,
            type: marker.type,
            originalAbsoluteY: marker.absoluteY,
            adjustedAbsoluteY: adjustedAbsoluteY,
            pdfHeight: pdfHeight,
            relativeY: relativeY,
            adjustment: marker.type === 'variable' ? '-12px' : 'none'
          });

          return {
            ...marker,
            x: Math.min(Math.max(relativeX, 0), 1),
            y: Math.min(Math.max(relativeY, 0), 1)
          };
        }
        return marker;
      });

      console.log('Recalculating coordinates for initial markers:', updatedMarkers);
      setFieldMarkers(updatedMarkers);
    }
  }, [numPages, pageProxies, initialFieldMarkers]);

  const renderFieldMarkers = (pageNumber: number) => {
    return fieldMarkers
      .filter(marker => marker.pageNumber === pageNumber)
      .map((marker) => {
        const pageElement = pageRefs.current[pageNumber];
        if (!pageElement) return null;

        // Get current rendered page dimensions
        const rect = pageElement.getBoundingClientRect();
        // Calculate position based on relative coordinates (scale-independent)
        const absoluteX = marker.x * rect.width;
        const absoluteY = marker.y * rect.height;
        const markerIndex = fieldMarkers.findIndex(m => m.id === marker.id) + 1;

        // Get variable config if it's a variable type
        const variableConfig = marker.type === 'variable' 
          ? PREDEFINED_VARIABLES.find(v => v.name === marker.variableName)
          : null;

        const isSignature = marker.type === 'signature';
        const isText = marker.type === 'text';
        const isVariable = marker.type === 'variable';
        
        // Determine display properties based on type
        let backgroundColor, borderColor, textColor, displayText, Icon, width, height, fontSize, iconSize;
        
        if (isSignature) {
          backgroundColor = 'rgba(179, 229, 252, 0.95)';
          borderColor = '#4fc3f7';
          textColor = '#0277bd';
          // Show "Initial" for initial fields, "Sign" for regular signatures
          displayText = marker.variableName === 'initial' ? 'Initial' : 'Sign';
          Icon = CreateIcon;
          width = 100;
          height = 35;
          fontSize = '11px';
          iconSize = '16px';
        } else if (isText) {
          backgroundColor = 'rgba(103, 58, 183, 0.15)';
          borderColor = '#673ab7';
          textColor = '#673ab7';
          displayText = marker.customText || 'Text';
          Icon = TextFieldsIcon;
          // Use auto width - let the text determine the width naturally
          width = 'auto';
          height = 24;
          fontSize = '9px';
          iconSize = '12px';
        } else {
          // Variable
          backgroundColor = `${variableConfig?.color || '#4caf50'}15`;
          borderColor = variableConfig?.color || '#4caf50';
          textColor = variableConfig?.color || '#4caf50';
          displayText = variableConfig?.label || 'Variable';
          Icon = variableConfig?.icon || PersonIcon;
          width = 130; // Increased width to prevent text wrapping for "Customer Name"
          height = 24;
          fontSize = '9px';
          iconSize = '12px';
        }

        return (
          <Box
            key={marker.id}
            onMouseDown={(e) => handleMarkerMouseDown(e, marker.id, pageNumber)}
            sx={{
              position: 'absolute',
              left: absoluteX,
              top: absoluteY,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              cursor: draggingMarker?.id === marker.id ? 'grabbing' : 'grab',
              userSelect: 'none'
            }}
          >
            {/* Main field marker */}
            <Box
              onClick={(e) => {
                e.stopPropagation();
              }}
              sx={{
                width: width,
                height: height,
                backgroundColor: backgroundColor,
                borderRadius: isSignature ? '4px' : '3px',
                border: isSignature ? `2px solid ${borderColor}` : `1.5px solid ${borderColor}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: isText || isVariable ? 'flex-start' : 'center', // Left-align text and variable markers to match PDF/DocuSign
                color: textColor,
                fontSize: fontSize,
                fontWeight: 600,
                boxShadow: isSignature ? `0 2px 8px ${borderColor}50` : `0 1px 4px ${borderColor}50`,
                transition: 'all 0.2s',
                position: 'relative',
                overflow: 'visible', // Allow remove button to show for all markers
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                px: isSignature ? 1 : 0,  // No horizontal padding for text/variable markers
                '&:hover': {
                  backgroundColor: isSignature ? 'rgba(179, 229, 252, 1)' : `${borderColor}25`,
                  borderColor: borderColor,
                  boxShadow: isSignature ? `0 4px 12px ${borderColor}60` : `0 2px 6px ${borderColor}60`,
                  '& .remove-btn': {
                    opacity: 1
                  }
                }
              }}
            >
              {isSignature && <Icon sx={{ fontSize: iconSize, mr: 0.5 }} />}
              <span>{displayText}</span>

              {/* Remove button */}
              <Box
                className="remove-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFieldMarker(marker.id);
                }}
                sx={{
                  position: 'absolute',
                  top: isSignature ? -8 : -6,
                  right: isSignature ? -8 : -6,
                  width: isSignature ? 20 : 16,
                  height: isSignature ? 20 : 16,
                  backgroundColor: '#f44336',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  opacity: 0,
                  transition: 'all 0.2s',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#d32f2f',
                    transform: 'scale(1.1)'
                  }
                }}
              >
                <ClearIcon sx={{ fontSize: isSignature ? '12px' : '10px' }} />
              </Box>
            </Box>

            {/* Marker number label */}
            <Typography
              sx={{
                mt: 0.3,
                fontSize: '8px',
                fontWeight: 'bold',
                color: '#1976d2',
                backgroundColor: 'rgba(255,255,255,0.9)',
                px: 0.5,
                py: 0.2,
                borderRadius: '2px',
                border: '1px solid #1976d2'
              }}
            >
              #{markerIndex}
            </Typography>
          </Box>
        );
      });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: '95vw' },
          height: { xs: '100vh', sm: '95vh' },
          maxWidth: 'none',
          maxHeight: 'none',
          margin: { xs: 0, sm: 'auto' },
          borderRadius: { xs: 0, sm: '12px' },
          boxShadow: { xs: 'none', sm: '0 8px 32px rgba(0,0,0,0.12)' }
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0',
        px: { xs: 2, sm: 3 },
        py: { xs: 1.5, sm: 2 },
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        backgroundColor: 'white'
      }}>
        <Typography
          variant="h6"
          sx={{
            flex: 1,
            fontSize: { xs: '15px', sm: '16px' },
            fontWeight: 500,
            color: '#333',
            mb: { xs: 1, sm: 0 },
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          {fileName}
        </Typography>

        <Box sx={{
          display: 'flex',
          gap: { xs: 1, sm: 1.5 },
          alignItems: 'center',
          width: { xs: '100%', sm: 'auto' },
          justifyContent: { xs: 'flex-end', sm: 'flex-end' }
        }}>
          <Button
            onClick={generatePreviewPdf}
            variant="outlined"
            size="small"
            startIcon={<VisibilityIcon />}
            disabled={fieldMarkers.filter(m => m.type === 'text' || m.type === 'variable').length === 0}
            sx={{
              borderColor: '#ddd',
              color: '#666',
              fontWeight: 400,
              px: 2,
              fontSize: '13px',
              textTransform: 'none',
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                borderColor: '#ccc'
              },
              '&:disabled': {
                borderColor: '#eee',
                color: '#ccc'
              }
            }}
          >
            Preview
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            size="small"
            startIcon={<SaveIcon />}
            disabled={fieldMarkers.length === 0}
            sx={{
              backgroundColor: '#2196f3',
              fontWeight: 400,
              px: 2.5,
              fontSize: '13px',
              textTransform: 'none',
              borderRadius: '6px',
              boxShadow: 'none',
              '&:hover': { 
                backgroundColor: '#1976d2',
                boxShadow: 'none'
              },
              '&:disabled': {
                backgroundColor: '#e0e0e0',
                color: '#999'
              }
            }}
          >
            Save
          </Button>
          <IconButton onClick={handleClose} size="small" sx={{ color: '#666', '&:hover': { backgroundColor: '#f5f5f5' } }}>
            <CloseIcon sx={{ fontSize: '20px' }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{
          py: 1.5,
          px: 2,
          borderBottom: '1px solid #e8e8e8',
          backgroundColor: '#fafafa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="body2" sx={{ fontSize: '13px', color: '#666' }}>
            {activeFieldType ? `Click on the document to place the field` : 'Select a field type to start'}
          </Typography>
          {fieldMarkers.length > 0 && (
            <Chip 
              label={`${fieldMarkers.length} field${fieldMarkers.length !== 1 ? 's' : ''}`}
              size="small"
              sx={{
                backgroundColor: '#e3f2fd',
                color: '#2196f3',
                fontWeight: 500,
                fontSize: '12px',
                height: '24px'
              }}
            />
          )}
        </Box>

        <Box sx={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'row',
          position: 'relative'
        }}>
          {/* Left Sidebar - Field Types */}
          <Paper sx={{
            width: { xs: 200, sm: 260 },
            borderRight: '1px solid #e0e0e0',
            backgroundColor: '#fafafa',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            boxShadow: 'none'
          }}>
            <Box sx={{ p: 2.5, borderBottom: '1px solid #e0e0e0', backgroundColor: 'white' }}>
              <Typography variant="h6" sx={{ color: '#333', fontSize: '16px', fontWeight: 500 }}>
                Fields
              </Typography>
            </Box>

            {/* Signature Fields Section */}
            <Box sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e8e8e8' }}>
              <Typography variant="caption" sx={{ fontWeight: 500, color: '#888', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5, display: 'block' }}>
                Signature Fields
              </Typography>
              <Box
                onClick={() => {
                  setActiveFieldType('signature');
                  setActiveVariableName(null);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.25,
                  mb: 0.5,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  backgroundColor: activeFieldType === 'signature' && activeVariableName === null ? '#e3f2fd' : 'transparent',
                  border: activeFieldType === 'signature' && activeVariableName === null ? '1px solid #2196f3' : '1px solid transparent',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: activeFieldType === 'signature' && activeVariableName === null ? '#e3f2fd' : '#f5f5f5'
                  }
                }}
              >
                <CreateIcon sx={{ fontSize: '18px', color: activeFieldType === 'signature' && activeVariableName === null ? '#2196f3' : '#666' }} />
                <Typography sx={{ fontSize: '14px', fontWeight: activeFieldType === 'signature' && activeVariableName === null ? 500 : 400, color: activeFieldType === 'signature' && activeVariableName === null ? '#2196f3' : '#333' }}>
                  Signature
                </Typography>
              </Box>
              <Box
                onClick={() => {
                  setActiveFieldType('signature');
                  setActiveVariableName('initial');
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.25,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  backgroundColor: activeFieldType === 'signature' && activeVariableName === 'initial' ? '#e3f2fd' : 'transparent',
                  border: activeFieldType === 'signature' && activeVariableName === 'initial' ? '1px solid #2196f3' : '1px solid transparent',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: activeFieldType === 'signature' && activeVariableName === 'initial' ? '#e3f2fd' : '#f5f5f5'
                  }
                }}
              >
                <ShortTextIcon sx={{ fontSize: '18px', color: activeFieldType === 'signature' && activeVariableName === 'initial' ? '#2196f3' : '#666' }} />
                <Typography sx={{ fontSize: '14px', fontWeight: activeFieldType === 'signature' && activeVariableName === 'initial' ? 500 : 400, color: activeFieldType === 'signature' && activeVariableName === 'initial' ? '#2196f3' : '#333' }}>
                  Initial
                </Typography>
              </Box>
            </Box>

            {/* Contact Information Section */}
            <Box sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e8e8e8' }}>
              <Typography variant="caption" sx={{ fontWeight: 500, color: '#888', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5, display: 'block' }}>
                Contact Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {PREDEFINED_VARIABLES.filter(v => !['initial'].includes(v.name)).map((variable) => {
                  const Icon = variable.icon;
                  const isActive = activeFieldType === 'variable' && activeVariableName === variable.name;
                  
                  return (
                    <Box
                      key={variable.name}
                      onClick={() => {
                        setActiveFieldType('variable');
                        setActiveVariableName(variable.name);
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        p: 1.25,
                        borderRadius: '6px',
                        cursor: 'pointer',
                        backgroundColor: isActive ? '#e3f2fd' : 'transparent',
                        border: isActive ? '1px solid #2196f3' : '1px solid transparent',
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: isActive ? '#e3f2fd' : '#f5f5f5'
                        }
                      }}
                    >
                      <Icon sx={{ fontSize: '18px', color: isActive ? '#2196f3' : '#666' }} />
                      <Typography sx={{ fontSize: '14px', fontWeight: isActive ? 500 : 400, color: isActive ? '#2196f3' : '#333' }}>
                        {variable.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Custom Input Section */}
            <Box sx={{ py: 1.5, px: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 500, color: '#888', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5, display: 'block' }}>
                Custom Input
              </Typography>
              <Box
                onClick={() => {
                  setActiveFieldType('text');
                  setActiveVariableName(null);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.25,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  backgroundColor: activeFieldType === 'text' ? '#e3f2fd' : 'transparent',
                  border: activeFieldType === 'text' ? '1px solid #2196f3' : '1px solid transparent',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: activeFieldType === 'text' ? '#e3f2fd' : '#f5f5f5'
                  }
                }}
              >
                <TextFieldsIcon sx={{ fontSize: '18px', color: activeFieldType === 'text' ? '#2196f3' : '#666' }} />
                <Typography sx={{ fontSize: '14px', fontWeight: activeFieldType === 'text' ? 500 : 400, color: activeFieldType === 'text' ? '#2196f3' : '#333' }}>
                  Text
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* PDF Viewer Area */}
          <Box sx={{
            flex: 1,
            overflow: 'auto',
            backgroundColor: '#ffffff',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: 3,
            position: 'relative'
          }}>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <Typography>Loading PDF...</Typography>
            </Box>
          )}

          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={<Typography>Loading PDF...</Typography>}
          >
            {numPages && Array.from({ length: numPages }, (_, i) => i + 1).map((pageNumber) => (
              <Box
                key={pageNumber}
                sx={{
                  mb: 4,
                  position: 'relative',
                  border: '2px solid #e0e0e0',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: activeFieldType ? '#1976d2' : '#e0e0e0',
                    boxShadow: activeFieldType ? '0 8px 30px rgba(25,118,210,0.15)' : '0 4px 20px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: -2,
                    left: -2,
                    backgroundColor: '#1976d2',
                    color: 'white',
                    px: 2,
                    py: 0.5,
                    borderRadius: '0 0 12px 0',
                    fontSize: '12px',
                    fontWeight: 600,
                    boxShadow: '0 2px 8px rgba(25,118,210,0.3)'
                  }}
                >
                  Page {pageNumber}
                </Box>
                <Box
                  ref={(el) => {
                    if (el) {
                      pageRefs.current[pageNumber] = el as HTMLDivElement;
                    }
                  }}
                  onClick={(e) => handlePageClick(e, pageNumber)}
                  onMouseMove={(e) => {
                    if (draggingMarker) {
                      handleMarkerDrag(e, pageNumber);
                    } else {
                      handlePageMouseMove(e, pageNumber);
                    }
                  }}
                  onMouseUp={handleMarkerMouseUp}
                  onMouseLeave={() => {
                    handlePageMouseLeave();
                    if (draggingMarker) {
                      handleMarkerMouseUp();
                    }
                  }}
                  sx={{
                    position: 'relative',
                    cursor: draggingMarker ? 'grabbing' : activeFieldType ? (activeFieldType === 'signature' 
                      ? 'crosshair'
                      : 'copy'
                    ) : 'default'
                  }}
                >
                  <Page
                    pageNumber={pageNumber}
                    scale={pageScale}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                  {renderFieldMarkers(pageNumber)}

                  {/* Hover Preview - Updated for all types */}
                  {hoverPosition && hoverPosition.pageNumber === pageNumber && (() => {
                    const variableConfig = hoverPosition.type === 'variable' 
                      ? PREDEFINED_VARIABLES.find(v => v.name === hoverPosition.variableName)
                      : null;
                    
                    const isSignature = hoverPosition.type === 'signature';
                    const isText = hoverPosition.type === 'text';
                    
                    let width, height, fontSize, iconSize, borderWidth, borderRadius, px, backgroundColor, borderColor, textColor, displayText, Icon;
                    
                    if (isSignature) {
                      width = 100;
                      height = 35;
                      fontSize = '11px';
                      iconSize = '16px';
                      borderWidth = '2px';
                      borderRadius = '4px';
                      px = 1;
                      backgroundColor = 'rgba(179, 229, 252, 0.7)';
                      borderColor = '#4fc3f7';
                      textColor = '#0277bd';
                      displayText = hoverPosition.variableName === 'initial' ? 'Initial' : 'Sign';
                      Icon = CreateIcon;
                    } else if (isText) {
                      width = 80;
                      height = 24;
                      fontSize = '9px';
                      iconSize = '12px';
                      borderWidth = '1.5px';
                      borderRadius = '3px';
                      px = 0; // No padding to match DocuSign
                      backgroundColor = 'rgba(103, 58, 183, 0.2)';
                      borderColor = '#673ab7';
                      textColor = '#673ab7';
                      displayText = 'Add Text';
                      Icon = TextFieldsIcon;
                    } else {
                      width = 130; // Increased to fit "Customer Name"
                      height = 24;
                      fontSize = '9px';
                      iconSize = '12px';
                      borderWidth = '1.5px';
                      borderRadius = '3px';
                      px = 0; // No padding to match DocuSign
                      backgroundColor = `${variableConfig?.color || '#4caf50'}25`;
                      borderColor = variableConfig?.color || '#4caf50';
                      textColor = variableConfig?.color || '#4caf50';
                      displayText = variableConfig?.label || 'Variable';
                      Icon = variableConfig?.icon || PersonIcon;
                    }

                    return (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: hoverPosition.x,
                          top: hoverPosition.y,
                          width,
                          height,
                          backgroundColor,
                          border: `${borderWidth} dashed ${borderColor}`,
                          borderRadius,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: isSignature ? 'center' : 'flex-start', // Left-align text/variables to match DocuSign
                          color: textColor,
                          fontSize,
                          fontWeight: 600,
                          pointerEvents: 'none',
                          px,
                          animation: 'pulse 1s infinite',
                          '@keyframes pulse': {
                            '0%': {
                              opacity: 0.6,
                              transform: 'scale(1)'
                            },
                            '50%': {
                              opacity: 0.9,
                              transform: 'scale(1.02)'
                            },
                            '100%': {
                              opacity: 0.6,
                              transform: 'scale(1)'
                            }
                          }
                        }}
                      >
                        {isSignature && <Icon sx={{ fontSize: iconSize, mr: 0.5 }} />}
                        <span>{displayText}</span>
                      </Box>
                    );
                  })()}
                </Box>
              </Box>
            ))}
          </Document>
          </Box>
        </Box>

        {/* Enhanced Zoom Controls - Mobile Responsive */}
        <Box sx={{
          position: 'absolute',
          bottom: {
            xs: fieldMarkers.length > 0 ? 140 : 20,
            sm: fieldMarkers.length > 0 ? 180 : 20
          },
          right: { xs: 10, sm: 20 },
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 0.5, sm: 0.75 },
          backgroundColor: 'white',
          borderRadius: '8px',
          p: 1,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          border: '1px solid #e8e8e8',
          transition: 'bottom 0.3s ease',
          zIndex: 1000
        }}>
          <Fab
            size="small"
            onClick={() => setPageScale(prev => Math.min(prev + 0.2, 3))}
            disabled={pageScale >= 3}
            sx={{
              backgroundColor: '#fff',
              color: '#666',
              border: '1px solid #ddd',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              '&:hover': { backgroundColor: '#f5f5f5' },
              '&:disabled': { backgroundColor: '#f9f9f9', color: '#ccc' }
            }}
          >
            +
          </Fab>
          <Typography variant="caption" sx={{ textAlign: 'center', px: 0.5, fontSize: '11px', color: '#666', fontWeight: 500 }}>
            {Math.round(pageScale * 100)}%
          </Typography>
          <Fab
            size="small"
            onClick={() => setPageScale(prev => Math.max(prev - 0.2, 0.5))}
            disabled={pageScale <= 0.5}
            sx={{
              backgroundColor: '#fff',
              color: '#666',
              border: '1px solid #ddd',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              '&:hover': { backgroundColor: '#f5f5f5' },
              '&:disabled': { backgroundColor: '#f9f9f9', color: '#ccc' }
            }}
          >
            -
          </Fab>
        </Box>
      </DialogContent>

      {/* Field Markers List - Mobile Responsive Horizontal Design */}
      {fieldMarkers.length > 0 && (
        <Box sx={{
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#fafafa',
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          minHeight: { xs: '100px', sm: '120px' },
          maxHeight: { xs: '120px', sm: '140px' },
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: { xs: 1, sm: 1.5 },
            flexWrap: { xs: 'wrap', sm: 'nowrap' }
          }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                color: '#333',
                fontSize: { xs: '13px', sm: '14px' }
              }}
            >
              Placed Fields ({fieldMarkers.length})
            </Typography>
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={() => {
                setFieldMarkers([]);
                setHoverPosition(null);
              }}
              sx={{
                color: '#666',
                fontSize: '12px',
                px: 1.5,
                py: 0.5,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              Clear All
            </Button>
          </Box>
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: { xs: 1, sm: 2 },
            maxHeight: { xs: '60px', sm: '80px' },
            overflow: 'auto',
            pb: 1,
            '&::-webkit-scrollbar': {
              height: { xs: '4px', sm: '6px' }
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#1976d2',
              borderRadius: '3px'
            }
          }}>
            {fieldMarkers.map((marker, index) => {
              const variableConfig = marker.type === 'variable' 
                ? PREDEFINED_VARIABLES.find(v => v.name === marker.variableName)
                : null;
              const isSignature = marker.type === 'signature';
              const isText = marker.type === 'text';
              
              let displayColor, displayText;
              if (isSignature) {
                displayColor = '#4fc3f7';
                displayText = marker.variableName === 'initial' ? 'Initial' : 'Sign';
              } else if (isText) {
                displayColor = '#673ab7';
                displayText = marker.customText || 'Text';
              } else {
                displayColor = variableConfig?.color || '#4caf50';
                displayText = variableConfig?.label || 'Variable';
              }

              return (
                <Box
                  key={marker.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 0.5, sm: 1 },
                    p: { xs: 0.5, sm: 1 },
                    backgroundColor: '#f8f9fa',
                    borderRadius: { xs: '6px', sm: '8px' },
                    border: `2px solid ${displayColor}`,
                    minWidth: { xs: '120px', sm: '160px' },
                    height: { xs: '40px', sm: 'auto' },
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: displayColor,
                      backgroundColor: `${displayColor}15`,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 4px 8px ${displayColor}50`
                    }
                  }}
                >
                  <Box sx={{
                    width: { xs: 24, sm: 32 },
                    height: { xs: 24, sm: 32 },
                    backgroundColor: displayColor,
                    borderRadius: { xs: '4px', sm: '6px' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: { xs: '10px', sm: '12px' },
                    fontWeight: 'bold',
                    minWidth: { xs: '24px', sm: '32px' }
                  }}>
                    #{index + 1}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: '11px', sm: '13px' },
                        lineHeight: 1.2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: displayColor
                      }}
                    >
                      {displayText}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{
                        fontSize: { xs: '8px', sm: '10px' },
                        lineHeight: 1,
                        display: { xs: 'none', sm: 'block' }
                      }}
                    >
                      Page {marker.pageNumber}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => removeFieldMarker(marker.id)}
                    sx={{
                      width: { xs: 20, sm: 24 },
                      height: { xs: 20, sm: 24 },
                    color: '#f44336',
                    p: 0,
                    '&:hover': {
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      transform: 'scale(1.1)'
                    }
                  }}
                >
                  <DeleteIcon sx={{ fontSize: { xs: '12px', sm: '14px' } }} />
                </IconButton>
              </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Inline Text Editor - renders directly on PDF */}
      {editingTextField && (
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            top: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            pointerEvents: 'none'
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: `${editingTextField.x}px`,
              top: `${editingTextField.y}px`,
              zIndex: 10000,
              pointerEvents: 'auto'
            }}
          >
            <TextField
              autoFocus
              size="small"
              variant="outlined"
              value={editingTextField.text}
              onChange={(e) => setEditingTextField(prev => prev ? {...prev, text: e.target.value} : null)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSaveEditingTextField();
                } else if (e.key === 'Escape') {
                  handleCancelEditingTextField();
                }
              }}
              onBlur={handleSaveEditingTextField}
              placeholder="Type text here..."
              sx={{
                backgroundColor: 'white',
                '& .MuiInputBase-root': {
                  fontSize: '11px',
                  minWidth: '120px',
                  paddingLeft: '0px' // Remove left padding to align text with box edge
                },
                '& .MuiInputBase-input': {
                  paddingLeft: '0px', // Remove input padding for precise alignment
                  paddingRight: '8px'
                }
              }}
            />
          </Box>
          <Box
            onClick={handleCancelEditingTextField}
            sx={{
              position: 'fixed',
              left: 0,
              top: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'transparent',
              pointerEvents: 'auto'
            }}
          />
        </Box>
      )}

      {/* PDF Preview Dialog */}
      <Dialog
        open={showPreview}
        onClose={() => {
          setShowPreview(false);
          setPreviewNumPages(null);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            borderRadius: '12px'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
          py: 2,
          backgroundColor: 'white'
        }}>
          <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 500, color: '#333' }}>
            Preview
          </Typography>
          <IconButton onClick={() => {
            setShowPreview(false);
            setPreviewNumPages(null);
          }} size="small" sx={{ color: '#666' }}>
            <CloseIcon sx={{ fontSize: '20px' }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, height: '100%', overflow: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', backgroundColor: '#f5f5f5' }}>
          {previewPdfUrl && (
            <Document
              file={previewPdfUrl}
              onLoadSuccess={(pdf) => {
                setPreviewNumPages(pdf.numPages);
                console.log('Preview PDF loaded:', pdf.numPages, 'pages');
              }}
              onLoadError={(error) => console.error('Error loading preview PDF:', error)}
              loading={
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography color="#666">Loading preview...</Typography>
                </Box>
              }
            >
              {Array.from(new Array(previewNumPages || 1), (el, index) => (
                <Box key={`preview-page-${index + 1}`} sx={{ mb: 2 }}>
                  <Page
                    pageNumber={index + 1}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    scale={1.2}
                  />
                </Box>
              ))}
            </Document>
          )}
        </DialogContent>
      </Dialog>

    </Dialog>
  );
};

export default PDFSignatureModal;